import type { Countries } from '~/libs/dato/queries/countries';
import type { Places } from '~/libs/dato/queries/places';
import type { Regions } from '~/libs/dato/queries/regions';

type NamedRecord = { id: string; name: string };

const buildEnglishToLocalisedMap = (localised: NamedRecord[], english: NamedRecord[]): Map<string, string> => {
  const localisedById = new Map(localised.map((r) => [r.id, r.name]));
  const map = new Map<string, string>();
  english.forEach((r) => {
    const localisedName = localisedById.get(r.id);
    if (localisedName) map.set(r.name, localisedName);
  });
  return map;
};

export type DestinationNameMaps = {
  countries: Map<string, string>; // English countryName -> localised countryName
  places: Map<string, string>; // English placeName -> localised placeName
  regions: Map<string, string>; // English regionName -> localised regionName
};

export const buildDestinationNameMaps = (
  localised: { countries: Countries['allCountries']; places: Places['allPlaces']; regions: Regions['allRegions'] },
  english: { countries: Countries['allCountries']; places: Places['allPlaces']; regions: Regions['allRegions'] }
): DestinationNameMaps => ({
  countries: buildEnglishToLocalisedMap(
    localised.countries
      .filter((c) => c.countryId != null && c.countryName != null)
      .map((c) => ({ id: String(c.countryId), name: c.countryName as string })),
    english.countries
      .filter((c) => c.countryId != null && c.countryName != null)
      .map((c) => ({ id: String(c.countryId), name: c.countryName as string }))
  ),
  places: buildEnglishToLocalisedMap(
    localised.places
      .filter((p) => p.placeId != null && p.placeName != null)
      .map((p) => ({ id: String(p.placeId), name: p.placeName as string })),
    english.places
      .filter((p) => p.placeId != null && p.placeName != null)
      .map((p) => ({ id: String(p.placeId), name: p.placeName as string }))
  ),
  regions: buildEnglishToLocalisedMap(
    localised.regions
      .filter((r) => r.regionId != null && r.regionName != null)
      .map((r) => ({ id: String(r.regionId), name: r.regionName as string })),
    english.regions
      .filter((r) => r.regionId != null && r.regionName != null)
      .map((r) => ({ id: String(r.regionId), name: r.regionName as string }))
  )
});

// Blueprint.country/places are comma-joined free text; region is a single string. Each part is
// looked up across all three maps (a destination's field — country/place/region — isn't tracked
// upstream) and falls back to itself if no match/translation exists, mirroring Playground's
// getEnglishPlaceName fallback-to-self behaviour. Country/region names colliding with a place
// name are not expected in Byway's European catalogue.
export const getLocalisedDestinationLabel = (value: string, maps: DestinationNameMaps): string =>
  value
    .split(', ')
    .map((part) => maps.countries.get(part) ?? maps.regions.get(part) ?? maps.places.get(part) ?? part)
    .join(', ');
